import type { ReactNode, RefObject } from "react";
import { Label } from "@/components/atoms/label";
import { DirectiveOptionFieldFreeForm } from "@/features/sparxsEditor/components/molecules/directiveOptionField/directiveOptionFieldFreeForm";
import { DirectiveOptionFieldAllowedValues } from "@/features/sparxsEditor/components/molecules/directiveOptionField/directiveOptionFieldAllowedValues";
import { DirectiveOptionFieldSuggestedValues } from "@/features/sparxsEditor/components/molecules/directiveOptionField/directiveOptionFieldSuggestedValues";
import type { DirectiveChoiceSlot } from "../../core/config";

export type DirectiveChoiceSlotControlVariant =
  | "floating"
  | "chip-edit"
  | "chip-next";

export type DirectiveChoiceSlotControlProps = {
  slot: DirectiveChoiceSlot | undefined;
  value: string;
  onCommit: (value: string) => void;
  fieldId: string;
  label: ReactNode;
  remountKey?: number;
  variant: DirectiveChoiceSlotControlVariant;
  suggestedInputRef?: RefObject<HTMLInputElement | null>;
  allowedTriggerRef?: RefObject<HTMLButtonElement | null>;

  floatingAllowedTriggerRef?: RefObject<HTMLButtonElement | null>;
  freeformInputRef?: RefObject<HTMLInputElement | null>;
  freeformAutoFocus?: boolean;
};

export const DirectiveChoiceSlotControl = ({
  slot,
  value,
  onCommit,
  fieldId,
  label,
  remountKey = 0,
  variant,
  suggestedInputRef,
  allowedTriggerRef,
  floatingAllowedTriggerRef,
  freeformInputRef,
  freeformAutoFocus,
}: DirectiveChoiceSlotControlProps): ReactNode => {
  if (slot == null) return null;
  const choiceOptions = slot.options != null ? [...slot.options] : null;
  const choiceSuggestedOptions =
    slot.suggestedOptions != null ? [...slot.suggestedOptions] : null;

  if (choiceSuggestedOptions != null) {
    return (
      <DirectiveOptionFieldSuggestedValues
        suggestedOptions={choiceSuggestedOptions}
        value={value}
        onChange={onCommit}
        label={label}
        id={fieldId}
        placeholder={slot.placeholder}
        inputRef={variant === "chip-next" ? suggestedInputRef : undefined}
      />
    );
  }
  if (choiceOptions != null) {
    const allowedAutoFocus = variant === "floating" || variant === "chip-next";
    const triggerRefForAllowed =
      variant === "chip-next"
        ? allowedTriggerRef
        : variant === "floating"
          ? floatingAllowedTriggerRef
          : undefined;
    return (
      <DirectiveOptionFieldAllowedValues
        key={remountKey}
        autoFocus={allowedAutoFocus}
        triggerRef={triggerRefForAllowed}
        options={choiceOptions}
        value={value}
        onChange={onCommit}
        label={label}
        id={fieldId}
        placeholder={slot.placeholder}
      />
    );
  }
  if (variant === "chip-next") {
    return (
      <>
        <Label variant="caption" htmlFor={fieldId} id={`${fieldId}-label`}>
          {slot.label}
          {slot.placeholder != null ? ` (${slot.placeholder})` : ""}
        </Label>
        <DirectiveOptionFieldFreeForm
          key={remountKey}
          id={fieldId}
          placeholder={slot.placeholder}
          onSubmit={onCommit}
          inputRef={freeformInputRef}
        />
      </>
    );
  }
  if (variant === "chip-edit") {
    return (
      <DirectiveOptionFieldFreeForm
        key={fieldId}
        placeholder={slot.placeholder}
        initialValue={value}
        onSubmit={onCommit}
      />
    );
  }
  return (
    <>
      <Label variant="caption" htmlFor={fieldId} id={`${fieldId}-label`}>
        {slot.label}
        {slot.placeholder != null ? ` (${slot.placeholder})` : ""}
      </Label>
      <DirectiveOptionFieldFreeForm
        key={remountKey}
        autoFocus={freeformAutoFocus ?? false}
        id={fieldId}
        placeholder={slot.placeholder}
        initialValue={value}
        onSubmit={onCommit}
      />
    </>
  );
};
